import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicNodeMaterial,
  PlaneGeometry,
  Vector3,
  type Color,
} from 'three/webgpu';
import { uv, vec2, vec3 } from 'three/tsl';

import { COLORS, EMISSIVE_GAIN } from '../core/constants';
import { TRANSPORT_STATES } from '../audio/Scheduler';
import { EDIT_MODES, RECORD_STATES, SAMPLE_MODES } from '../store/panelTypes';
import { usePanelStore } from '../store/panelStore';
import { useInstrumentStore } from './instrumentStore';
import { SCREEN_RECTS, uvRect, type Rect } from './screen/layout';
import type { ScreenTexture } from './screen/ScreenTexture';

const SELECTION_BUTTONS = Array.from({ length: 12 }, (_, i) => `Button_Select_${i + 1}`);

/** How fast a sliding marker closes on its target, per second. */
const SLIDE_EASE = 14;

function additiveMaterial(color: Color): MeshBasicNodeMaterial {
  const material = new MeshBasicNodeMaterial();
  material.colorNode = vec3(color.r, color.g, color.b).mul(EMISSIVE_GAIN);
  material.blending = AdditiveBlending;
  material.transparent = true;
  material.depthWrite = false;
  material.side = DoubleSide;
  material.toneMapped = false;
  return material;
}

/** A flat triangle pointing down, used as the edit-mode caret. */
function triangleGeometry(width: number, height: number): BufferGeometry {
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    'position',
    new BufferAttribute(
      new Float32Array([-width / 2, height, 0, width / 2, height, 0, 0, 0, 0]),
      3,
    ),
  );
  geometry.computeVertexNormals();
  return geometry;
}

/**
 * Material that shows one region of the instrument display, so the progress
 * rings drawn into the atlas can also float above the button they belong to.
 */
function regionMaterial(screen: ScreenTexture, rect: Rect): MeshBasicNodeMaterial {
  const [offsetX, offsetY, repeatX, repeatY] = uvRect(rect);
  const material = new MeshBasicNodeMaterial();
  material.colorNode = screen.node
    .sample(uv().mul(vec2(repeatX, repeatY)).add(vec2(offsetX, offsetY)))
    .rgb.mul(EMISSIVE_GAIN);
  material.blending = AdditiveBlending;
  material.transparent = true;
  material.depthWrite = false;
  material.side = DoubleSide;
  material.toneMapped = false;
  return material;
}

/**
 * The lit overlays that sit on top of the panel: the sliding sample and
 * arpeggio markers, the edit caret, and the progress rings that hover over the
 * record, overwrite, demo and loop buttons.
 *
 * These are separate from the instrument model — they are pure state feedback,
 * and keeping them out of the GLB means the panel state drives them directly.
 */
export function Indicators({ screen }: { screen: ScreenTexture }) {
  const buttons = useInstrumentStore((state) => state.buttons);
  const ready = useInstrumentStore((state) => state.ready);

  const caret = useRef<Mesh>(null);
  const sampleMarker = useRef<Group>(null);
  const arpMarker = useRef<Group>(null);
  const vibratoMarker = useRef<Mesh>(null);
  const arpModeMarker = useRef<Mesh>(null);
  const sampleRing = useRef<Group>(null);
  const overwriteRing = useRef<Group>(null);
  const musicRing = useRef<Group>(null);
  const demoRing = useRef<Group>(null);

  const geometry = useMemo(
    () => ({
      caret: triangleGeometry(0.25, 0.125),
      bar: new PlaneGeometry(0.35, 0.025),
      ring: new PlaneGeometry(0.7, 0.7),
    }),
    [],
  );

  const materials = useMemo(
    () => ({
      caret: additiveMaterial(COLORS.green),
      sample: additiveMaterial(COLORS.white),
      arp: additiveMaterial(COLORS.darkblue),
      vibrato: additiveMaterial(COLORS.yellow),
      arpMode: additiveMaterial(COLORS.darkblue),
      record: regionMaterial(screen, SCREEN_RECTS.RECORD_PROGRESS),
      music: regionMaterial(screen, SCREEN_RECTS.MUSIC_PROGRESS),
      demo: regionMaterial(screen, SCREEN_RECTS.DEMO_PROGRESS),
    }),
    [screen],
  );

  const target = useMemo(() => ({ sample: new Vector3(), arp: new Vector3() }), []);

  useFrame((state, delta) => {
    if (!ready) return;
    const panel = usePanelStore.getState();
    const ease = 1 - Math.exp(-SLIDE_EASE * delta);

    const sampleButton = buttons.get(SELECTION_BUTTONS[panel.SAMPLE_SELECTION_VALUE]);
    const arpButton = buttons.get(SELECTION_BUTTONS[panel.ARP_SELECTION_VALUE]);
    if (sampleButton) target.sample.copy(sampleButton.position);
    if (arpButton) target.arp.copy(arpButton.position);

    if (sampleMarker.current) {
      sampleMarker.current.position.x += (target.sample.x - sampleMarker.current.position.x) * ease;
      sampleMarker.current.position.z += (target.sample.z - sampleMarker.current.position.z) * ease;
    }
    if (arpMarker.current) {
      arpMarker.current.position.x += (target.arp.x - arpMarker.current.position.x) * ease;
      arpMarker.current.position.z += (target.arp.z - arpMarker.current.position.z) * ease;
      arpMarker.current.visible = panel.ARP_ENABLED;
    }

    if (caret.current) {
      caret.current.visible = panel.EDIT_MODE !== EDIT_MODES.DEFAULT;
      caret.current.position.y = 0.6 + (1 + Math.sin(state.clock.elapsedTime * 4)) * 0.05;
    }

    if (vibratoMarker.current) vibratoMarker.current.visible = panel.VIBRATO;
    if (arpModeMarker.current) {
      arpModeMarker.current.visible = panel.SELECTION_MODE === EDIT_MODES.ARP_SELECT;
    }

    const sampling =
      panel.RECORD_STATE !== RECORD_STATES.STOPPED && panel.SAMPLE_MODE === SAMPLE_MODES.RECORD;
    const overwriting =
      panel.RECORD_STATE !== RECORD_STATES.STOPPED && panel.SAMPLE_MODE === SAMPLE_MODES.OVERWRITE;
    if (sampleRing.current) sampleRing.current.visible = sampling;
    if (overwriteRing.current) overwriteRing.current.visible = overwriting;
    if (demoRing.current) demoRing.current.visible = panel.DEMO_STATE === TRANSPORT_STATES.PLAYING;
    if (musicRing.current) {
      musicRing.current.visible = panel.MUSIC_RECORDER_STATE !== TRANSPORT_STATES.STOPPED;
    }
  });

  if (!ready) return null;

  const at = (name: string): [number, number, number] => {
    const mesh = buttons.get(name);
    return mesh ? [mesh.position.x, mesh.position.y, mesh.position.z] : [0, 0, 0];
  };

  const flat = -Math.PI / 2;

  return (
    <group>
      <mesh
        ref={caret}
        geometry={geometry.caret}
        material={materials.caret}
        position={[-3.15, 0.6, -1.1]}
        rotation-x={(-30 * Math.PI) / 180}
      />

      <group ref={sampleMarker} position-y={0.47}>
        <mesh geometry={geometry.bar} material={materials.sample} rotation-x={flat} />
      </group>

      <group ref={arpMarker} position-y={0.47}>
        <mesh geometry={geometry.bar} material={materials.arp} rotation-x={flat} position-z={0.03} />
      </group>

      <group position={at('Button_Vibrato')}>
        <mesh
          ref={vibratoMarker}
          geometry={geometry.bar}
          material={materials.vibrato}
          rotation-x={flat}
          position-y={0.1}
          visible={false}
        />
      </group>

      <group position={at('Button_ModeSelect_ArpSelect')}>
        <mesh
          ref={arpModeMarker}
          geometry={geometry.bar}
          material={materials.arpMode}
          rotation-x={flat}
          position-y={0.1}
          visible={false}
        />
      </group>

      <group ref={sampleRing} position={at('Button_Sample')} visible={false}>
        <Ring geometry={geometry.ring} material={materials.record} />
      </group>
      <group ref={overwriteRing} position={at('Button_Overwrite')} visible={false}>
        <Ring geometry={geometry.ring} material={materials.record} />
      </group>
      <group ref={musicRing} position={at('Button_MP_Record')} visible={false}>
        <Ring geometry={geometry.ring} material={materials.music} />
      </group>
      <group ref={demoRing} position={at('Button_Demo_StartStop')} visible={false}>
        <Ring geometry={geometry.ring} material={materials.demo} />
      </group>
    </group>
  );
}

/** A progress quad laid almost flat over its button. */
function Ring({
  geometry,
  material,
}: {
  geometry: PlaneGeometry;
  material: MeshBasicNodeMaterial;
}) {
  return (
    <mesh
      geometry={geometry}
      material={material}
      position-y={0.1}
      rotation-x={(-85 * Math.PI) / 180}
      rotation-z={Math.PI / 2}
    />
  );
}
